/**
 * 发单监控看板的共享片段。
 * 「时间线」与「聊天界面」两种视图共用这里的行模型、覆盖率徽章、群命中详情与媒体预览。
 */
import { useCallback, useState, type ReactNode } from "react";
import { App, Modal, Spin, Tag, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, StopOutlined } from "../icons";
import { trpc } from "../../trpc";
import type {
  DispatchAggregateItem,
  DispatchCollectItem,
  DispatchDetectionReport,
  DispatchRoomRef,
  DispatchRoomStat,
  DispatchSenderItem,
  DispatchTargetGroupItem,
  DispatchTargetMessageItem,
} from "../../../shared/dispatchTypes";

const { Text } = Typography;

/** 一条发单链路上，某个目标分组内命中该 matchKey 的消息 */
export type TargetMatchItem = {
  groupId: string;
  groupName: string;
  businessUnitId: string;
  businessUnitName: string;
  roomCount: number;
  item: DispatchTargetMessageItem;
};

/** 对齐后的链路行：同一条 matchKey 的源头 / 采集 / 发单 */
export type AlignedRow = {
  key: string;
  source: DispatchAggregateItem | null;
  collect: DispatchCollectItem | null;
  targets: TargetMatchItem[];
  earliestTs: number;
  latestTs: number;
};

/** 报告是否覆盖了多个业务单元（聚合视图） */
export function isAggregatedReport(report: DispatchDetectionReport) {
  return (report.businessUnits?.length || 0) > 1;
}

export function buildAlignedRows(report: DispatchDetectionReport): AlignedRow[] {
  const sourceMap = new Map<string, DispatchAggregateItem>();
  const collectMap = new Map<string, DispatchCollectItem>();
  const targetMap = new Map<string, TargetMatchItem[]>();
  const earliestMap = new Map<string, number>();
  const latestMap = new Map<string, number>();

  const track = (matchKey: string, earliestTs: number, latestTs: number) => {
    const prevEarliest = earliestMap.get(matchKey);
    earliestMap.set(matchKey, prevEarliest === undefined ? earliestTs : Math.min(prevEarliest, earliestTs));
    latestMap.set(matchKey, Math.max(latestMap.get(matchKey) || 0, latestTs));
  };

  for (const item of report.sourceMessages) {
    sourceMap.set(item.matchKey, item);
    track(item.matchKey, item.earliestTs, item.latestTs);
  }

  for (const item of report.collectMessages) {
    collectMap.set(item.matchKey, item);
    track(item.matchKey, item.earliestTs, item.latestTs);
  }

  for (const group of report.targetGroups) {
    for (const item of group.messages) {
      const bucket = targetMap.get(item.matchKey) || [];
      bucket.push({
        groupId: group.groupId,
        groupName: group.groupName,
        businessUnitId: group.businessUnitId,
        businessUnitName: group.businessUnitName,
        roomCount: group.roomCount,
        item,
      });
      targetMap.set(item.matchKey, bucket);
      track(item.matchKey, item.earliestTs, item.latestTs);
    }
  }

  const keys = new Set([...sourceMap.keys(), ...collectMap.keys(), ...targetMap.keys()]);
  return Array.from(keys)
    .map((key) => ({
      key,
      source: sourceMap.get(key) || null,
      collect: collectMap.get(key) || null,
      targets: (targetMap.get(key) || []).sort((a, b) => a.groupName.localeCompare(b.groupName, "zh-CN")),
      earliestTs: earliestMap.get(key) || 0,
      latestTs: latestMap.get(key) || 0,
    }))
    .sort((a, b) => b.latestTs - a.latestTs);
}

export function formatClockTime(timestamp: number) {
  return new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(timestamp * 1000));
}

export function formatTimeRange(start: number, end: number) {
  if (!start && !end) return "-";
  if (start === end) return formatClockTime(start);
  return `${formatClockTime(start)} - ${formatClockTime(end)}`;
}

export function senderLabel(sender: DispatchSenderItem) {
  return sender.name || sender.id;
}

const WECOM_USER_ID_PATTERN = /^(wm|wo)[A-Za-z0-9_-]{10,}$/;

/** 没能解析出昵称的发送人，标签就是一串企微 userId。 */
export function isRawSenderId(label: string) {
  return WECOM_USER_ID_PATTERN.test(label);
}

export interface GroupedSender {
  /** 代表 id（只用于 React key；服务端已按 userId 去重，这里按昵称聚合）。 */
  id: string;
  label: string;
  /** 同名账号数（>1 才展示 “×N”）。 */
  count: number;
  /** 各账号群数之和。拿不到去重后的群集合，所以不宣称是“去重群数”。 */
  roomCount: number;
  /** 悬停说明，合并项才有。 */
  title: string;
}

/**
 * 按展示名把发送人聚合起来（“黄先生×2”）。
 *
 * 服务端的 senders 是按 userId 去重的，所以同一个人的两个号、
 * 或两个号取了同一个昵称，都会在界面上重复出现同一串名字。
 * 保留首次出现的顺序（服务端已按群数降序排过）。
 */
export function groupSenders(senders: DispatchSenderItem[]): GroupedSender[] {
  const groups = new Map<string, GroupedSender>();

  for (const sender of senders) {
    const label = senderLabel(sender);
    const existing = groups.get(label);
    if (existing) {
      existing.count += 1;
      existing.roomCount += sender.roomCount || 0;
      continue;
    }
    groups.set(label, {
      id: sender.id,
      label,
      count: 1,
      roomCount: sender.roomCount || 0,
      title: "",
    });
  }

  return [...groups.values()].map((group) =>
    group.count > 1
      ? { ...group, title: `${group.label}：${group.count} 个账号，合计 ${group.roomCount} 个群（未去重）` }
      : group
  );
}

/* ------------------------------------------------------------------ */
/* 消息内容                                                            */
/* ------------------------------------------------------------------ */

const MEDIA_PREVIEW_TYPES = new Set(["image", "emotion"]);
const MEDIA_LABELS: Record<string, string> = { image: "[图片]", emotion: "[表情]" };

export interface MessageContentLike {
  sourceId?: string;
  matchKey?: string;
  msgtype?: string;
  content?: string;
  mediaKey?: string;
  mediaMd5?: string;
}

/**
 * 本地 mock 预览专用的媒体地址覆盖。
 * 线上不会调用它，因此始终走 /api/archive-media；dev 预览页没有存档服务，先用内联图片顶替。
 */
let mediaUrlOverride: ((item: MessageContentLike) => string) | null = null;

export function setMediaUrlOverride(resolver: ((item: MessageContentLike) => string) | null) {
  mediaUrlOverride = resolver;
}

export function archiveMediaUrl(item: MessageContentLike) {
  if (mediaUrlOverride) return mediaUrlOverride(item);
  if (!item.sourceId || !item.mediaMd5 || !MEDIA_PREVIEW_TYPES.has(item.msgtype || "")) return "";
  return `/api/archive-media/${encodeURIComponent(item.sourceId)}/${encodeURIComponent(item.mediaMd5)}`;
}

export type ResolvedMessageContent = {
  msgtype: string;
  isMedia: boolean;
  mediaUrl: string;
  text: string;
  /** 媒体文件不可用时的占位文案，例如 [图片] */
  mediaLabel: string;
};

/**
 * 解析一条消息该展示什么。
 *
 * 媒体消息只展示媒体本身：matchKey（形如 `image|[图片]|<md5>`）是内部分组键，
 * 不是给人看的正文；msgtype 徽标在媒体消息上也冗余，一并去掉。
 * 拿不到原始文件时退化为 `[图片]` 占位并显式提示不可用，不用 hash 糊过去。
 */
export function resolveMessageContent(item: MessageContentLike): ResolvedMessageContent {
  const msgtype = item.msgtype || "";
  const isMedia = MEDIA_PREVIEW_TYPES.has(msgtype);
  const mediaUrl = archiveMediaUrl(item);
  const text = isMedia ? "" : item.content || item.mediaKey || msgtype || "-";
  return {
    msgtype,
    isMedia,
    mediaUrl,
    text,
    mediaLabel: MEDIA_LABELS[msgtype] || "[媒体]",
  };
}

export function MessageContentView({
  item,
  className,
  variant = "cell",
  onPreview,
}: {
  item: MessageContentLike;
  className?: string;
  /** bubble（聊天气泡）会隐去 text 这类无信息量的类型徽标，只留有区分度的类型 */
  variant?: "cell" | "bubble";
  onPreview: (preview: { url: string; title: string }) => void;
}) {
  const { msgtype, isMedia, mediaUrl, text, mediaLabel } = resolveMessageContent(item);
  const showMsgtype = !!msgtype && !isMedia && !(variant === "bubble" && msgtype === "text");
  return (
    <div className={className}>
      {showMsgtype ? <span className="dispatch-board-msgtype">{msgtype}</span> : null}
      {isMedia ? (
        mediaUrl ? (
          <button
            type="button"
            className="dispatch-board-media-link"
            onClick={() => onPreview({ url: mediaUrl, title: mediaLabel })}
          >
            <img src={mediaUrl} alt={mediaLabel} className="dispatch-board-media-preview" loading="lazy" />
          </button>
        ) : (
          <div className="dispatch-board-content-text dispatch-board-content-text--muted">
            {mediaLabel} 原始文件不可用
          </div>
        )
      ) : (
        <div className="dispatch-board-content-text">{text}</div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 覆盖率徽章                                                          */
/* ------------------------------------------------------------------ */

export type CoverageStatus = "complete" | "partial" | "none";

function resolveCoverageStatus(matchedCount: number, totalCount: number): CoverageStatus {
  if (totalCount <= 0) return "none";
  if (matchedCount >= totalCount) return "complete";
  if (matchedCount > 0) return "partial";
  return "none";
}

const coverageMeta: Record<CoverageStatus, { label: string; icon: typeof CheckCircleOutlined }> = {
  complete: { label: "已发全", icon: CheckCircleOutlined },
  partial: { label: "未发全", icon: StopOutlined },
  none: { label: "未命中", icon: CloseCircleOutlined },
};

/**
 * 覆盖率状态 → 文案 / 图标。
 * 时间线的大徽章与聊天界面 footer 里的紧凑统计共用这一份映射，避免两处漂移。
 */
export function coverageStatusMeta(matchedCount: number, totalCount: number) {
  const status = resolveCoverageStatus(matchedCount, totalCount);
  return { status, label: coverageMeta[status].label, Icon: coverageMeta[status].icon };
}

/** 越差越小，用来从多个目标分组里取最差状态。 */
const COVERAGE_RANK: Record<CoverageStatus, number> = { none: 0, partial: 1, complete: 2 };

/** 同一行里一个目标分组的覆盖率 */
export type RowCoverage = {
  groupId: string;
  groupName: string;
  /** 该分组在行内的命中情况；target 为 null 表示这条消息压根没发到这个分组 */
  target: TargetMatchItem | null;
  matchedCount: number;
  totalCount: number;
  messageCount: number;
  status: CoverageStatus;
};

/**
 * 列表里要的纯文本正文。
 * 媒体消息的 text 是空的（气泡里只展示媒体本身），但列表里没有图片位，
 * 直接留空会变成一条“空消息”，所以退化成 `[图片]` 这类占位。
 */
export function messageContentLabel(item: MessageContentLike): string {
  const resolved = resolveMessageContent(item);
  if (!resolved.isMedia) return resolved.text;
  return resolved.mediaUrl ? resolved.mediaLabel : `${resolved.mediaLabel} 原始文件不可用`;
}

/** 一条链路行的消息体：优先源头，其次采集，最后任意一个发单命中。 */
export function rowMessageLabel(row: AlignedRow): string {
  const item: MessageContentLike | null = row.source || row.collect || row.targets[0]?.item || null;
  if (!item) return "-";
  return messageContentLabel(item);
}

/**
 * 一条链路行在各目标分组上的覆盖率。
 * totalCount 的口径与聊天界面 footer 的统计完全一致（targetConfiguredRoomCount 回退到分组群数）。
 */
export function rowCoverage(row: AlignedRow, targetGroups: DispatchTargetGroupItem[]): RowCoverage[] {
  return targetGroups.map((group) => {
    const target = row.targets.find((item) => item.groupId === group.groupId) || null;
    const matchedCount = target?.item.targetHitRoomCount || 0;
    const totalCount = target ? target.item.targetConfiguredRoomCount || group.roomCount : group.roomCount;
    return {
      groupId: group.groupId,
      groupName: group.groupName,
      target,
      matchedCount,
      totalCount,
      messageCount: target?.item.targetMessageCount || 0,
      status: resolveCoverageStatus(matchedCount, totalCount),
    };
  });
}

/** 一条没发全的链路：哪个分组、差多少。 */
export type CoverageIssue = {
  key: string;
  row: AlignedRow;
  /** 所有没发全的分组，按严重程度从差到好 */
  gaps: RowCoverage[];
  /** 这一行里最差的覆盖率状态 */
  worst: CoverageStatus;
  /** 该行涉及的全部目标分组（用来展示 “1/3” 这类总数） */
  coverage: RowCoverage[];
};

/**
 * 漏发清单：只看“没发全”的行。
 * totalCount <= 0 的分组（压根没配置发单群）不算漏发，否则会把“没配置”说成“没发”。
 */
export function collectCoverageIssues(
  rows: AlignedRow[],
  targetGroups: DispatchTargetGroupItem[]
): CoverageIssue[] {
  if (targetGroups.length === 0) return [];

  const issues: CoverageIssue[] = [];
  for (const row of rows) {
    const coverage = rowCoverage(row, targetGroups).filter((entry) => entry.totalCount > 0);
    const gaps = coverage.filter((entry) => entry.status !== "complete");
    if (gaps.length === 0) continue;
    issues.push({
      key: row.key,
      row,
      coverage,
      gaps: [...gaps].sort((a, b) => COVERAGE_RANK[a.status] - COVERAGE_RANK[b.status]),
      worst: gaps.reduce(
        (worst, entry) => (COVERAGE_RANK[entry.status] < COVERAGE_RANK[worst] ? entry.status : worst),
        "complete" as CoverageStatus
      ),
    });
  }
  return issues;
}

export function DispatchCoverageBadge({
  matchedCount,
  totalCount,
  onClick,
  size = "default",
}: {
  matchedCount: number;
  totalCount: number;
  onClick?: () => void;
  size?: "default" | "large";
}) {
  const { status, label, Icon } = coverageStatusMeta(matchedCount, totalCount);

  return (
    <button
      type="button"
      className={`dispatch-coverage-badge dispatch-coverage-badge--${status} dispatch-coverage-badge--${size}${onClick ? "" : " dispatch-coverage-badge--static"}`}
      onClick={onClick}
      title={onClick ? "点击查看群命中详情" : undefined}
    >
      <Icon className="dispatch-coverage-badge__icon" />
      <span className="dispatch-coverage-badge__ratio">
        <span className="dispatch-coverage-badge__matched">{matchedCount}</span>
        <span className="dispatch-coverage-badge__sep">/</span>
        <span className="dispatch-coverage-badge__total">{totalCount}</span>
      </span>
      <span className="dispatch-coverage-badge__label">{label}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* 小片段                                                              */
/* ------------------------------------------------------------------ */

export function CellSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="dispatch-board-section">
      <div className="dispatch-board-section-label">{label}</div>
      <div className="dispatch-board-section-body">{children}</div>
    </div>
  );
}

export function SenderList({ senders }: { senders: DispatchSenderItem[] }) {
  if (senders.length === 0) return <Text type="secondary">未知发送人</Text>;
  return (
    <div className="dispatch-board-sender-list">
      {groupSenders(senders).map((sender) => (
        <span
          key={sender.id}
          className="dispatch-board-sender-chip"
          title={sender.title || undefined}
        >
          <span className="dispatch-board-sender-name">{sender.label}</span>
          {sender.count > 1 ? (
            <span className="dispatch-board-sender-count">×{sender.count}</span>
          ) : null}
          {sender.roomCount > 1 ? (
            <span className="dispatch-board-sender-count">{sender.roomCount} 群</span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

export function RoomChipList({ rooms }: { rooms: DispatchRoomRef[] }) {
  if (rooms.length === 0) return <Text type="secondary">未配置</Text>;
  return (
    <div className="dispatch-board-room-chips">
      {rooms.map((room) => (
        <span key={room.roomId} className="dispatch-board-room-chip" title={room.roomName}>
          {room.roomName}
        </span>
      ))}
    </div>
  );
}

export function RoomHitTag({ roomName, count, received }: { roomName: string; count: number; received: boolean }) {
  return (
    <Tag
      className={`dispatch-board-room-hit-tag ${received ? "dispatch-board-room-hit-tag--received" : "dispatch-board-room-hit-tag--missing"}`}
      title={roomName}
    >
      {roomName} · {count} 条
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* 群命中详情 / 媒体预览                                                */
/* ------------------------------------------------------------------ */

export type RoomDetails = {
  groupName: string;
  matchedRooms: DispatchRoomStat[];
  missingRooms: DispatchRoomRef[];
  matchedCount: number;
  totalRoomCount: number;
};

export type RoomDetailState = RoomDetails & { title: string };

export type LoadRoomDetails = (
  target: TargetMatchItem,
  report: DispatchDetectionReport
) => Promise<RoomDetails>;

export const defaultLoadRoomDetails: LoadRoomDetails = async (target, report) => {
  // 聚合视图下目标群已跨单元合并，详情也要按整个集合查，不能挂在单个单元分组下
  if (isAggregatedReport(report)) {
    return trpc.fadan.detectionRooms.query({
      businessUnitIds: report.businessUnits.map((unit) => unit.id),
      matchKeyHash: target.item.matchKeyHash,
      windowMinutes: report.windowMinutes,
    });
  }
  return trpc.fadan.detectionRooms.query({
    businessUnitId: target.businessUnitId,
    groupId: target.groupId,
    matchKeyHash: target.item.matchKeyHash,
    windowMinutes: report.windowMinutes,
  });
};

export function roomDetailTitle(target: TargetMatchItem, report: DispatchDetectionReport) {
  return isAggregatedReport(report) ? target.groupName : `${target.businessUnitName} · ${target.groupName}`;
}

export function useDispatchBoardModals(
  report: DispatchDetectionReport,
  loadRoomDetails: LoadRoomDetails = defaultLoadRoomDetails
) {
  const { message } = App.useApp();
  const [roomModal, setRoomModal] = useState<RoomDetailState | null>(null);
  const [roomModalLoading, setRoomModalLoading] = useState(false);
  const [mediaModal, setMediaModal] = useState<{ url: string; title: string } | null>(null);

  const openTargetRooms = useCallback(
    async (target: TargetMatchItem) => {
      const title = roomDetailTitle(target, report);
      setRoomModal({
        title,
        groupName: title,
        matchedRooms: [],
        missingRooms: [],
        matchedCount: target.item.targetHitRoomCount || 0,
        totalRoomCount: target.item.targetConfiguredRoomCount || target.roomCount,
      });
      setRoomModalLoading(true);
      try {
        const detail = await loadRoomDetails(target, report);
        setRoomModal({ ...detail, title });
      } catch (error: any) {
        setRoomModal(null);
        message.error(error?.message || "加载群命中详情失败");
      } finally {
        setRoomModalLoading(false);
      }
    },
    [loadRoomDetails, message, report]
  );

  return {
    roomModal,
    roomModalLoading,
    openTargetRooms,
    closeRoomModal: useCallback(() => setRoomModal(null), []),
    mediaModal,
    openMediaPreview: setMediaModal,
    closeMediaPreview: useCallback(() => setMediaModal(null), []),
  };
}

export function DispatchRoomDetailModal({
  state,
  loading,
  onClose,
}: {
  state: RoomDetailState | null;
  loading: boolean;
  onClose: () => void;
}) {
  return (
    <Modal open={!!state} title={state ? `${state.title} 群命中情况` : ""} onCancel={onClose} footer={null} width={480}>
      <Spin spinning={loading}>
        {state ? (
          <div className="dispatch-board-room-modal">
            <div className="dispatch-board-room-modal-section">
              <Text strong className="dispatch-board-room-modal-label">
                未收到 ({state.missingRooms.length})
              </Text>
              <div className="dispatch-board-room-hit-tags">
                {state.missingRooms.length === 0 ? (
                  <Text type="secondary" className="dispatch-board-room-modal-empty">
                    无
                  </Text>
                ) : (
                  state.missingRooms.map((room) => (
                    <RoomHitTag key={room.roomId} roomName={room.roomName} count={0} received={false} />
                  ))
                )}
              </div>
            </div>
            <div className="dispatch-board-room-modal-section">
              <Text strong className="dispatch-board-room-modal-label">
                已收到 ({state.matchedRooms.length})
              </Text>
              <div className="dispatch-board-room-hit-tags">
                {state.matchedRooms.length === 0 ? (
                  <Text type="secondary" className="dispatch-board-room-modal-empty">
                    无
                  </Text>
                ) : (
                  state.matchedRooms.map((room) => (
                    <RoomHitTag key={room.roomId} roomName={room.roomName} count={room.count} received />
                  ))
                )}
              </div>
            </div>
            <div className="dispatch-board-room-modal-summary">
              <DispatchCoverageBadge
                matchedCount={state.matchedCount}
                totalCount={state.totalRoomCount}
                size="large"
              />
            </div>
          </div>
        ) : null}
      </Spin>
    </Modal>
  );
}

export function DispatchMediaPreviewModal({
  preview,
  onClose,
}: {
  preview: { url: string; title: string } | null;
  onClose: () => void;
}) {
  return (
    <Modal
      open={!!preview}
      title={preview?.title || "媒体预览"}
      onCancel={onClose}
      footer={null}
      width="min(900px, 92vw)"
      centered
    >
      {preview ? (
        <div className="dispatch-media-preview-modal">
          <img src={preview.url} alt={preview.title} className="dispatch-media-preview-modal-image" />
        </div>
      ) : null}
    </Modal>
  );
}
